/**
 * Sent-tab email preview — Outlook-style layout.
 *
 *  ┌─────────────────────────────────────────────────────────────────────────────────┐
 *  │ [↩ Reply] [Open] [App]   [Response] [Resend] [Stop]    date                     │
 *  ├─────────────────────────────────────────────────────────────────────────────────┤
 *  │ To <recipient>                                              <status>            │
 *  │ <subject>                                                                       │
 *  ├─────────────────────────────────────────────────────────────────────────────────┤
 *  │ <email body — scrollable, tracking pixels hidden>                               │
 *  └─────────────────────────────────────────────────────────────────────────────────┘
 */

import { Link } from 'react-router-dom';
import {
  CheckCircle,
  ExternalLink,
  HelpCircle,
  Mail,
  Reply,
  RotateCcw,
  StopCircle,
  XCircle,
} from 'lucide-react';
import type { EmailThread } from '@core/types';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { emailDeliveryDotColor, emailDeliveryKind, emailDeliveryLabel } from './emailDeliveryUi';
import { StatusChip } from '@shared/components/ui/StatusChip';

const CTA_ICON: Record<string, React.ReactNode> = {
  accept: <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />,
  reject: <XCircle className="h-3.5 w-3.5 text-red-400" />,
  query: <HelpCircle className="h-3.5 w-3.5 text-amber-400" />,
};

const CTA_LABEL: Record<string, string> = {
  accept: 'Shortlisted',
  reject: 'Passed',
  query: 'Query',
};

const CTA_COLOR: Record<string, string> = {
  accept: '#10B981',
  reject: '#EF4444',
  query: '#F59E0B',
};

interface Props {
  thread: EmailThread;
  onStopFollowup?: (id: string) => void;
  onRetrigger?: (thread: EmailThread) => void;
  /** When set, Reply opens the composer in the right panel (Sent tab) instead of navigating away. */
  onInlineReply?: () => void;
}

function cleanEmailHtml(html: string): string {
  return html
    .replace(/\{\{TRACK_PIXEL\}\}/gi, '')
    .replace(/<img[^>]*track[^>]*>/gi, '')
    .replace(/<img[^>]*1x1[^>]*>/gi, '')
    .replace(/<img[^>]*width=["']?1["']?[^>]*height=["']?1["']?[^>]*>/gi, '')
    .trim();
}

export function EmailThreadPreviewPanel({ thread, onStopFollowup, onRetrigger, onInlineReply }: Props) {
  const kind = emailDeliveryKind(thread);
  const displayName =
    thread.recipient_name?.trim() || thread.recipient_email || '—';
  const timestamp = thread.sent_at ?? thread.created_at;
  const isResponded = !!thread.cta_response;
  const canStopFollowup = !isResponded && !thread.followup_stopped;

  const replyHref = `/emails/compose?applicationId=${encodeURIComponent(
    thread.application_id,
  )}&replyToThread=${encodeURIComponent(thread.id)}`;

  const replyButton = onInlineReply ? (
    <button
      type="button"
      onClick={onInlineReply}
      className="inline-flex h-7 items-center gap-1.5 rounded-[6px] px-3 text-[11px] font-medium text-white"
      style={{ background: '#10B981' }}
    >
      <Reply className="h-3.5 w-3.5" aria-hidden />
      Reply
    </button>
  ) : (
    <Link
      to={replyHref}
      className="inline-flex h-7 items-center gap-1.5 rounded-[6px] px-3 text-[11px] font-medium text-white"
      style={{ background: '#10B981' }}
    >
      <Reply className="h-3.5 w-3.5" aria-hidden />
      Reply
    </Link>
  );

  const cleanedBody = thread.body_html
    ? cleanEmailHtml(thread.body_html)
    : '<p style="color:var(--vscode-text-tertiary)">No body.</p>';

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      {/* Top toolbar */}
      <div
        className="flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2"
        style={{
          borderColor: '#6B7280',
          background: 'var(--vscode-bg-secondary)',
        }}
      >
        {replyButton}
        <Link
          to={`/emails/thread/${thread.id}`}
          className="inline-flex h-7 items-center gap-1.5 rounded-[6px] border px-2 text-[10.5px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            color: 'var(--vscode-text-secondary)',
          }}
        >
          <Mail className="h-3 w-3" aria-hidden />
          Open thread
        </Link>
        {thread.application_id ? (
          <Link
            to={`/applications/${thread.application_id}`}
            className="inline-flex h-7 items-center gap-1.5 rounded-[6px] border px-2 text-[10.5px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-secondary)',
            }}
          >
            <ExternalLink className="h-3 w-3" aria-hidden />
            Application
          </Link>
        ) : null}

        {/* Right side: Response + Resend + Stop followup + date */}
        <div className="ml-auto flex items-center gap-2">
          {isResponded && thread.cta_response ? (
            <span
              className="inline-flex h-7 items-center gap-1.5 rounded-[6px] border px-2 text-[10.5px] font-medium"
              style={{
                borderColor: CTA_COLOR[thread.cta_response] || 'var(--vscode-border-primary)',
                color: CTA_COLOR[thread.cta_response] || 'var(--vscode-text-secondary)',
              }}
            >
              {CTA_ICON[thread.cta_response]}
              {CTA_LABEL[thread.cta_response]}
            </span>
          ) : null}
          {thread.sent_at && onRetrigger ? (
            <button
              type="button"
              onClick={() => onRetrigger(thread)}
              className="inline-flex h-7 items-center gap-1.5 rounded-[6px] border px-2 text-[10.5px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
              style={{
                borderColor: 'var(--vscode-border-primary)',
                color: '#3B82F6',
              }}
              title="Resend email"
            >
              <RotateCcw className="h-3 w-3" aria-hidden />
              Resend
            </button>
          ) : null}
          {canStopFollowup && onStopFollowup ? (
            <button
              type="button"
              onClick={() => onStopFollowup(thread.id)}
              className="inline-flex h-7 items-center gap-1.5 rounded-[6px] border px-2 text-[10.5px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
              style={{
                borderColor: 'var(--vscode-border-primary)',
                color: '#EF4444',
              }}
              title="Stop follow-ups"
            >
              <StopCircle className="h-3 w-3" aria-hidden />
              Stop
            </button>
          ) : null}
          <span className="text-[10.5px] tabular-nums vscode-text-tertiary">
            {timestamp ? formatDateTimeGmt(timestamp) : ''}
          </span>
        </div>
      </div>

      {/* Header — To recipient + subject + status */}
      <div
        className="flex shrink-0 flex-col gap-0.5 border-b px-3 py-2"
        style={{ borderColor: '#6B7280' }}
      >
        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 truncate text-[12px]">
            <span className="vscode-text-tertiary">To </span>
            <span className="font-medium vscode-text-primary">{displayName}</span>
          </p>
          <StatusChip label={emailDeliveryLabel(kind)} dotColor={emailDeliveryDotColor(kind)} />
        </div>
        {thread.subject ? (
          <h2 className="truncate text-[12px] font-semibold leading-snug vscode-text-primary">
            {thread.subject}
          </h2>
        ) : null}
      </div>

      {/* Body — scrollable */}
      <div className="min-h-0 min-w-0 flex-1 overflow-auto px-3 py-3">
        <div
          className={[
            'text-[13px] leading-[1.6] vscode-text-primary',
            '[&_*]:!max-w-full',
            '[&_table]:!w-full [&_table]:!max-w-full [&_table]:!border-collapse',
            '[&_td]:!max-w-full [&_td]:break-words',
            '[&_img]:!h-auto [&_img]:!max-w-full',
            '[&_img[width="1"]]:!hidden [&_img[height="1"]]:!hidden',
            '[&_pre]:whitespace-pre-wrap [&_pre]:break-words',
            '[&_a]:text-[var(--vscode-text-link)] [&_a]:underline',
            '[&_p]:my-1.5',
            '[&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5',
            '[&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5',
          ].join(' ')}
          dangerouslySetInnerHTML={{ __html: cleanedBody }}
        />
      </div>
    </div>
  );
}
